





































import {
  computed, defineComponent, onUnmounted, PropType, reactive, ref, watch,
} from '@vue/composition-api';
import { Mod } from '@c/landingpage/modpack/types';
import { useBreakpoint } from '@a/plugins/breakpoint';
import BaseModpackListItem from '@c/landingpage/modpack/BaseModpackListItem.vue';
import { TouchDirection, TouchTravelled } from '@a/directive/ui/touchswipe/types';
import Vue from 'vue';
import BaseModpackControls from '@c/landingpage/modpack/BaseModpackControls.vue';
import { debounce } from '@a/helper/event-control';

export default defineComponent({
  emits: ['on-mod-selected'],
  name: 'BaseModpackList',
  components: { BaseModpackControls, BaseModpackListItem },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    /**
     * the actual list of {@link Mod}
     */
    value: {
      type: Array as PropType<Array<Mod>>,
      required: true,
    },
    /**
     * disables basically everything (clicking, swiping, etc.)
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup: (props, { emit }) => {
    const { isLargeSize, isExtraLargeSize, isMediumSize } = useBreakpoint();
    const state = reactive({
      touchTraveledX: 0 as number,
      isSwiping: false,
      page: 0,
    });

    const modpackItem = ref<Array<Vue>>(null);
    const modItemRightMargin = 5;
    const modItemLeftMargin = 5;
    const mobileItemsPerRow = 1;
    const tabletItemsPerRow = 3;
    const desktopItemsPerRow = 3;
    const mobileRows = 2;
    const desktopRows = 3;
    const threshold = 0.25;

    /**
     * does magically manages the movement 👻
     */
    const listPositioningStyle = computed(() => (
      { transform: `translate3d(${(state.page * (getAverageModpackItemWidth() * itemsPerRow.value) + state.touchTraveledX) * -1}px, 0px, 0px)` }
    ));

    /**
     * like the name says it returns true if its a large screen
     */
    const isBigScreen = computed<boolean>(
      () => isLargeSize.value || isExtraLargeSize.value || isMediumSize.value,
    );

    /**
     * returns the amount of rows
     */
    const rows = computed(() => (
      isBigScreen.value
        ? desktopRows
        : mobileRows
    ));

    /**
     * returns how many items should be displayed (desktop 3, mobile 1)
     */
    const itemsPerRow = computed<number>(() => (
      isMediumSize.value
        ? tabletItemsPerRow
        : isBigScreen.value
          ? desktopItemsPerRow
          : mobileItemsPerRow
    ));

    /**
     * returns the average pixel width of our modpack items ($el component)
     */
    const getAverageModpackItemWidth = (): number => {
      if (!modpackItem.value) return 0;

      const sum = modpackItem.value
        .map((item) => {
          const element = item.$el as HTMLDivElement;

          return element.offsetWidth + modItemRightMargin + modItemLeftMargin;
        })
        .reduce((a, b) => a + b);

      return (sum / modpackItem.value.length) || 0;
    };

    /**
     * jumps to a specific page and set the correct dimensions
     * @param page
     */
    const jumpToPage = (page: number): void => {
      if (page < 0) {
        throw new TypeError('Page shall not be negative');
      }

      state.page = page;
    };

    /**
     * returns the total amount of pages keeping the responsive list amounts and itemsPerRow in
     * mind
     */
    const totalPages = computed<number>(
      () => Math.ceil(props.value.length / rows.value / itemsPerRow.value) - 1,
    );

    /**
     * returns true when the last page has been reached
     */
    const isLastPage = computed<boolean>(
      () => state.page >= totalPages.value,
    );

    /**
     * returns true when the first page is active
     */
    const isFirstPage = computed<boolean>(
      () => state.page === 0,
    );

    const onResizeHandle = debounce(() => {
      // --- if after a resize the current page is higher than the total page we set him to the
      // --- last page, otherwise to the current page
      jumpToPage(state.page > totalPages.value
        ? totalPages.value
        : state.page);
    }, 250);

    window.addEventListener('resize', onResizeHandle);

    onUnmounted(() => {
      window.removeEventListener('resize', onResizeHandle);
    });

    /**
     * calculates the next page by using the actual travelled pixel
     *
     * @param travelled
     */
    const calculateNextPage = (travelled: TouchTravelled): number => {
      switch (travelled.direction) {
        case TouchDirection.LEFT:
          return state.page >= totalPages.value ? totalPages.value : state.page + 1;

        case TouchDirection.RIGHT:
          return state.page < 0 ? state.page : state.page - 1;

        default:
          return state.page;
      }
    };

    /**
     * saving the last abscissa vector to make use of if by the next drag
     * @param el
     * @param travelled
     */
    const onTouchEnd = (el: HTMLElement, travelled: TouchTravelled): void => {
      const requiredTouchActions = [TouchDirection.RIGHT, TouchDirection.LEFT];
      const requiredTouchForNextPage = getAverageModpackItemWidth() * itemsPerRow.value * threshold;

      state.isSwiping = false;
      state.touchTraveledX = 0;

      // --- correct touch action AND threeshold check
      if (requiredTouchActions.includes(travelled.direction)
          && Math.abs(travelled.x) >= requiredTouchForNextPage) {
        jumpToPage(calculateNextPage(travelled));
        return;
      }

      jumpToPage(state.page);
    };

    /**
     * setting the current abscissa vector to make it "move"
     * @param el
     * @param travelled
     */
    const onTouchMove = (el: HTMLElement, travelled: TouchTravelled) => {
      state.isSwiping = true;

      state.touchTraveledX = travelled.x;
    };

    /**
     * when mods changes navigate to page 1
     */
    const valueWatchStopHandle = watch(() => props.value, () => {
      jumpToPage(0);
    });

    /**
     * handle keydown events (left and right arrow)
     * @param event
     */
    const keyDownEventListener = (event) => {
      if (event.key === 'ArrowLeft' && !isFirstPage.value) {
        jumpToPage(state.page - 1);
      } else if (event.key === 'ArrowRight' && !isLastPage.value) {
        jumpToPage(state.page + 1);
      }
    };

    window.addEventListener('keydown', keyDownEventListener);

    onUnmounted(() => {
      valueWatchStopHandle();
      window.removeEventListener('keydown', keyDownEventListener);
    });

    return {
      listPositioningStyle,
      itemsPerRow,
      emit,
      rows,
      jumpToPage,
      modpackItem,
      onTouchMove,
      onTouchEnd,
      state,
      isLargeSize,
      isExtraLargeSize,
      isMediumSize,
      isBigScreen,
      isLastPage,
      isFirstPage,
      totalPages,
    };
  },
});
